import i18next, { type BackendModule, type ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { isUiLanguage, safeStorage, type UiLanguage } from '@/shared/lib';

/**
 * Локализация (localization.md): все строки — через t(), неймспейс = домен,
 * локали — locales/{ru,en,es}/<ns>.json, загружаются лениво (отдельные чанки Vite).
 * Язык интерфейса — состояние i18next (единственный источник), сохраняется в safe storage.
 */
export const CORE_NAMESPACES = [
  'common',
  'errors',
  'auth',
  'shell',
  'registry',
  'card',
  'form',
  'dialogs',
] as const;
export type CoreNamespace = (typeof CORE_NAMESPACES)[number];

const LANGUAGE_STORAGE_KEY = 'ui.language';

/*
 * Без generic-параметра у import.meta.glob: файловый кэш трансформаций Vitest пропускает модули,
 * в тексте которых есть ровно «import.meta.glob(», — иначе новый файл локали не попал бы в тесты
 * до очистки кэша. Модуль локали проверяется на границе (объект в default).
 */
const localeLoaders = import.meta.glob('./locales/*/*.json');

function localeResources(module: unknown): Record<string, unknown> | null {
  if (typeof module !== 'object' || module === null || !('default' in module)) return null;
  const resources = module.default;
  if (typeof resources !== 'object' || resources === null) return null;
  return Object.fromEntries(Object.entries(resources));
}

const lazyLocalesBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read(language: string, namespace: string, callback: ReadCallback) {
    const load = localeLoaders[`./locales/${language}/${namespace}.json`];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (module) => callback(null, localeResources(module) ?? {}),
      (error: unknown) =>
        callback(error instanceof Error ? error : new Error(String(error)), false),
    );
  },
};

export const i18n = i18next.createInstance();

/** Сохранённый выбор языка или язык браузера (если поддерживается), иначе ru. */
export function detectInitialLanguage(): UiLanguage {
  const stored = safeStorage.get(LANGUAGE_STORAGE_KEY);
  if (isUiLanguage(stored)) return stored;
  if (typeof navigator !== 'undefined') {
    const browser = navigator.language.slice(0, 2).toLowerCase();
    if (isUiLanguage(browser)) return browser;
  }
  return 'ru';
}

export async function initI18n(language: UiLanguage = detectInitialLanguage()): Promise<void> {
  if (i18n.isInitialized) {
    await changeUiLanguage(language, { persist: false });
    return;
  }
  await i18n
    .use(lazyLocalesBackend)
    .use(initReactI18next)
    .init({
      lng: language,
      supportedLngs: ['ru', 'en', 'es'],
      fallbackLng: { es: ['en'], en: ['ru'], default: ['ru'] },
      ns: [...CORE_NAMESPACES],
      defaultNS: 'common',
      interpolation: { escapeValue: false },
      returnEmptyString: false,
      react: { useSuspense: true },
    });
  setDocumentLanguage(language);
}

function setDocumentLanguage(language: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

export function getUiLanguage(): UiLanguage {
  const current = i18n.resolvedLanguage ?? i18n.language;
  return isUiLanguage(current) ? current : 'ru';
}

export async function changeUiLanguage(
  language: UiLanguage,
  options: { persist?: boolean } = {},
): Promise<void> {
  if (options.persist !== false) safeStorage.set(LANGUAGE_STORAGE_KEY, language);
  if (i18n.language !== language) await i18n.changeLanguage(language);
  setDocumentLanguage(language);
}
